import React, { useState } from 'react'
import { pantryColors, pantryRadii, pantryShadows } from '@pantry/ui'
import type { Product, ProductDraft } from '../../../../types/product'
import { SyncPosDataDialog } from '../modals/SyncPosDataDialog'
import { useProductDetailContext } from '../../hooks/useProductDetailContext'
import { useProductPosSync } from '../../hooks/useProductPosSync'

interface PosDataCardProps {
  product: Product
  draft: ProductDraft
  onPatch: (patch: Partial<ProductDraft>) => void
  onEditInPos?: () => void
}

const cardStyle: React.CSSProperties = {
  background: pantryColors.white,
  border: `1px solid ${pantryColors.gray[200]}`,
  borderRadius: pantryRadii.lg,
  boxShadow: pantryShadows.sm,
  overflow: 'hidden',
}

const headStyle: React.CSSProperties = {
  padding: '12px 16px',
  borderBottom: `1px solid ${pantryColors.gray[200]}`,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
}

const fieldStyle: React.CSSProperties = {
  padding: '7px 0',
  borderBottom: `1px solid ${pantryColors.gray[50]}`,
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: 8,
}

const editableFieldStyle: React.CSSProperties = {
  ...fieldStyle,
  alignItems: 'center',
}

const labelStyle: React.CSSProperties = {
  fontSize: 11,
  color: pantryColors.gray[400],
  whiteSpace: 'nowrap',
  flexShrink: 0,
}

const valueStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  color: pantryColors.gray[700],
  textAlign: 'right',
  wordBreak: 'break-all',
}

const emptyValueStyle: React.CSSProperties = {
  ...valueStyle,
  color: pantryColors.gray[400],
  fontStyle: 'italic',
  fontWeight: 400,
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: 140,
  boxSizing: 'border-box',
  padding: '5px 8px',
  border: `1px solid ${pantryColors.gray[200]}`,
  borderRadius: pantryRadii.sm,
  fontSize: 12,
  color: pantryColors.gray[800],
  background: pantryColors.white,
  fontFamily: 'inherit',
  textAlign: 'right',
  outline: 'none',
}

const syncBtnStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '4px 8px',
  borderRadius: pantryRadii.sm,
  border: `1px solid ${pantryColors.gray[200]}`,
  background: pantryColors.white,
  color: pantryColors.gray[600],
  fontSize: 11,
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'inherit',
}

export const PosDataCard: React.FC<PosDataCardProps> = ({ product, draft, onPatch, onEditInPos }) => {
  const { variationGroup, storeId, storeType } = useProductDetailContext()
  const { syncFromPos, isSyncing: isSyncMutationPending } = useProductPosSync(storeId, storeType)
  const [isSyncDialogOpen, setIsSyncDialogOpen] = useState(false)

  const isManual = product.posStatus === 'disconnect'
  const isSyncing = isSyncMutationPending || Boolean(product.waitingSyncingFromPos)
  const statusLabel = isSyncing
    ? 'Syncing data from POS…'
    : isManual
      ? 'Manually edited'
      : 'Data synced from POS'
  const statusDotColor = isSyncing
    ? pantryColors.amber[700]
    : isManual
      ? pantryColors.gray[400]
      : pantryColors.brand.primary

  const handleConfirmSync = () => {
    syncFromPos(product.id)
    setIsSyncDialogOpen(false)
  }

  const integrationLabel = product.posIntegration
    ? product.posIntegration.charAt(0).toUpperCase() + product.posIntegration.slice(1)
    : 'POS'

  const collectShortSku = (v: unknown): string => {
    if (!v || typeof v !== 'object') return ''
    const rec = v as Record<string, unknown>
    const raw = rec.short_sku ?? rec.shortSku ?? rec.sku
    return typeof raw === 'string' ? raw.trim() : ''
  }

  const sources: unknown[] = [
    ...(variationGroup ?? []),
    ...(draft.variationData ?? []),
  ]
  const variationSkus = Array.from(
    new Set(sources.map(collectShortSku).filter(Boolean)),
  ).join(', ')

  // Cova exposes the POS product name via the variation-group endpoint's
  // catalog.MasterName; Greenline maps it into posProductName from the detail's
  // variation_data[0].gl_product_name (see greenline gateway). This is the
  // POS-side name — never fall back to the storefront item.name; a missing
  // payload just renders '—'. Optional-chain so it can't crash.
  const posProductName =
    storeType === 'cova'
      ? variationGroup?.[0]?.catalog?.MasterName || variationGroup?.[0]?.catalog?.MasterProductName
      : product.posProductName

  const readOnlyRows: { label: string; value: string | undefined | null }[] = [
    { label: 'Product Name', value: posProductName },
    { label: 'POS SKU', value: variationSkus || product.posSku },
    // { label: 'HC Classification', value: product.hcClass },
  ]

  return (
    <>
    <div style={cardStyle}>
      <div style={headStyle}>
        <div
          style={{
            width: 24,
            height: 24,
            borderRadius: 6,
            background: pantryColors.amber[50],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke={pantryColors.amber[700]} strokeWidth="2" style={{ width: 12, height: 12 }}>
            <rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" />
          </svg>
        </div>
        <h4 style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', flex: 1, margin: 0, color: pantryColors.gray[600] }}>
          POS Data
        </h4>
        <span
          style={{
            fontSize: 10,
            fontWeight: 600,
            padding: '1px 7px',
            borderRadius: 999,
            background: pantryColors.amber[50],
            color: pantryColors.amber[700],
            border: `1px solid ${pantryColors.amber[200] ?? '#fde68a'}`,
          }}
        >
          {integrationLabel}
        </span>
      </div>

      <div style={{ padding: '6px 16px 14px' }}>
        {readOnlyRows.map(({ label, value }) => (
          <div key={label} style={fieldStyle}>
            <span style={labelStyle}>{label}</span>
            <span style={value ? valueStyle : emptyValueStyle}>
              {value || '—'}
            </span>
          </div>
        ))}

        <div style={editableFieldStyle}>
          <span style={labelStyle}>GTIN</span>
          <input
            type="text"
            value={draft.gtin}
            onChange={(e) => onPatch({ gtin: e.target.value })}
            placeholder="Enter GTIN…"
            style={inputStyle}
            aria-label="GTIN"
          />
        </div>

        <div style={editableFieldStyle}>
          <span style={labelStyle}>UPC</span>
          <input
            type="text"
            value={draft.upc}
            onChange={(e) => onPatch({ upc: e.target.value })}
            placeholder="Enter UPC…"
            style={inputStyle}
            aria-label="UPC"
          />
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingTop: 10,
            marginTop: 4,
            borderTop: `1px solid ${pantryColors.gray[50]}`,
          }}
        >
          <span style={{ fontSize: 11, color: pantryColors.gray[500], display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: statusDotColor, display: 'inline-block' }} />
            {statusLabel}
          </span>

          {isManual && (
            <button
              type="button"
              onClick={() => setIsSyncDialogOpen(true)}
              disabled={isSyncing}
              style={{
                ...syncBtnStyle,
                ...(isSyncing ? { opacity: 0.5, cursor: 'default' } : {}),
              }}
              aria-label="Sync data from POS"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 12, height: 12 }}>
                <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
              </svg>
              Sync
            </button>
          )}
        </div>

        {onEditInPos && (
          <div style={{ marginTop: 10 }}>
            <button
              type="button"
              onClick={onEditInPos}
              style={{
                width: '100%',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                padding: '7px 12px',
                borderRadius: 6,
                border: `1px solid ${pantryColors.gray[200]}`,
                background: pantryColors.white,
                color: pantryColors.gray[600],
                fontSize: 12,
                fontWeight: 500,
                cursor: 'pointer',
                fontFamily: 'inherit',
                transition: 'all .12s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = pantryColors.gray[300]; e.currentTarget.style.background = pantryColors.gray[50] }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = pantryColors.gray[200]; e.currentTarget.style.background = pantryColors.white }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 12, height: 12 }}>
                <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
              </svg>
              Edit in {integrationLabel} POS
            </button>
          </div>
        )}
      </div>
    </div>

    <SyncPosDataDialog
      open={isSyncDialogOpen}
      isSaving={isSyncMutationPending}
      onClose={() => setIsSyncDialogOpen(false)}
      onConfirm={handleConfirmSync}
    />
    </>
  )
}
